import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SNAPCHAT_LIMITS, SNAPCHAT_OPTIMIZATION_GOALS, defaultSnapConversion, defaultSnapDelivery, defaultSnapTargeting, snapGoalFor } from '../components/adglobe/snapchatConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const readError = async (response) => {
  try {
    const data = await response.json();
    return data.detail || data.message || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
};

export const validateSnapchatForm = (formData) => {
  const errors = [];
  if (!formData.campaignName) errors.push('Campaign name is required');
  if (!formData.objective || !SNAPCHAT_OPTIMIZATION_GOALS[formData.objective]) errors.push('Select a Snapchat campaign objective');
  if (!formData.budgetAmount || parseFloat(formData.budgetAmount) < 5) errors.push('Snapchat requires a minimum budget of 5 (account currency)');
  if (!formData.startDate) errors.push('Start date is required');
  if (formData.budgetType === 'lifetime' && !formData.endDate) errors.push('End date is required for lifetime budget');
  if (!formData.latitude || !formData.longitude) errors.push('Please select a location on the globe');
  if (!formData.snapBrandName) errors.push('Brand name is required');
  else if (formData.snapBrandName.length > SNAPCHAT_LIMITS.brandName) errors.push(`Brand name must be ${SNAPCHAT_LIMITS.brandName} characters or fewer`);
  if (!formData.headline) errors.push('Headline is required');
  else if (formData.headline.length > SNAPCHAT_LIMITS.headline) errors.push(`Headline must be ${SNAPCHAT_LIMITS.headline} characters or fewer`);
  if (!formData.imageFile && !formData.videoFile) errors.push('Snapchat requires a vertical (9:16) image or video');
  if (formData.destinationUrl && !/^https?:\/\//i.test(formData.destinationUrl)) errors.push('Destination URL must start with http:// or https://');
  const delivery = formData.snapDelivery || defaultSnapDelivery();
  if (delivery.bidStrategy !== 'AUTO_BID' && !(parseFloat(delivery.bidAmount) > 0)) errors.push('Enter a bid amount for Max Bid / Target Cost bidding');
  if (delivery.placement === 'CUSTOM' && delivery.positions.length === 0) errors.push('Select at least one Snapchat placement');
  const goal = snapGoalFor(formData.objective, formData.snapOptimizationGoal);
  const conv = { ...defaultSnapConversion(), ...(formData.snapConversion || {}) };
  if (goal?.needs === 'pixel') {
    if (!conv.pixelId) errors.push('Select a Snap Pixel for this optimization goal');
    if (!formData.destinationUrl) errors.push('Destination URL is required for pixel-based goals');
  }
  if (goal?.needs === 'lead_form' && !conv.leadFormId) errors.push('Select a Snapchat lead form');
  if (goal?.needs === 'app') {
    if (!conv.snapAppId) errors.push('Select your Snap App ID');
    if (!conv.appName) errors.push('App name is required');
    if (!conv.iosAppId && !conv.androidAppUrl) errors.push('Enter an iOS App ID or Android package name');
    if (!conv.iconMediaId) errors.push('Upload a square app icon');
  }
  return errors;
};

const toStartIso = (dateStr) => {
  const date = new Date(dateStr);
  const minStart = new Date(Date.now() + 5 * 60 * 1000);
  return (date < minStart ? minStart : date).toISOString();
};

export const buildSnapTargetingPayload = (formData) => {
  const t = { ...defaultSnapTargeting(), ...(formData.snapTargeting || {}) };
  return {
    country_code: 'ng',
    locations: formData.latitude && formData.longitude ? [{ latitude: formData.latitude, longitude: formData.longitude, radius_km: formData.radius || 5 }] : [],
    min_age: parseInt(formData.minAge) || 18,
    max_age: parseInt(formData.maxAge) || null,
    gender: formData.gender || 'ALL',
    languages: t.languages,
    region_ids: t.regionIds,
    interests_include: t.interestsInclude,
    interests_exclude: t.interestsExclude,
    segments_include: t.segmentsInclude,
    segments_exclude: t.segmentsExclude,
    os_types: t.osTypes,
    connection_types: t.connectionTypes,
    carrier_ids: t.carrierIds,
    auto_expansion: t.autoExpansion,
    smart_targeting: t.autoExpansion && t.smartTargeting,
    regulated_content: t.regulatedContent,
  };
};

export const useSnapchatAds = (setPlatformSettings) => {
  const [connection, setConnection] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [targetingOptions, setTargetingOptions] = useState(null);
  const [segments, setSegments] = useState([]);
  const [isLoadingTargeting, setIsLoadingTargeting] = useState(false);
  const [audienceEstimate, setAudienceEstimate] = useState({ status: 'idle' });
  const [conversionAssets, setConversionAssets] = useState(null);
  const [publicProfiles, setPublicProfiles] = useState([]);
  const [profileScopeNeeded, setProfileScopeNeeded] = useState(false);
  const [selectedProfileId, setSelectedProfileIdState] = useState('');

  const fetchPublicProfiles = useCallback(async (organizationId) => {
    if (!organizationId) return;
    try {
      const response = await fetch(`${API_URL}/api/snapchat/public-profiles/${organizationId}`);
      if (!response.ok) throw new Error(await readError(response));
      const data = await response.json();
      setPublicProfiles(data.profiles || []);
      setProfileScopeNeeded(!!data.needs_profile_scope);
    } catch (error) {
      console.error('Snapchat public profiles unavailable:', error.message);
    }
  }, []);

  const saveDefaultProfile = useCallback(async (profileId, profileName) => {
    const id = (profileId || '').trim();
    setSelectedProfileIdState(id);
    if (id.length < 8) return;
    try {
      const response = await fetch(`${API_URL}/api/snapchat/profile-default`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile_id: id, profile_name: profileName || null }),
      });
      if (!response.ok) {
        setSelectedProfileIdState('');
        toast.error('Not a valid Public Profile ID', { description: await readError(response), duration: 8000 });
      }
    } catch (error) {
      console.error('Could not save default Snapchat profile:', error.message);
    }
  }, []);
  const estimateTimer = useRef(null);

  const estimateAudience = useCallback((formData, accountId) => {
    if (!accountId) return;
    clearTimeout(estimateTimer.current);
    setAudienceEstimate((prev) => ({ ...prev, status: 'loading' }));
    estimateTimer.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/snapchat/audience-size/${accountId}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(buildSnapTargetingPayload(formData)),
        });
        if (!response.ok) throw new Error(await readError(response));
        const data = await response.json();
        setAudienceEstimate({ status: 'ready', min: data.audience_size_minimum, max: data.audience_size_maximum, at: Date.now() });
      } catch (error) {
        setAudienceEstimate({ status: 'error', error: error.message });
      }
    }, 900);
  }, []);

  const fetchConversionAssets = useCallback(async (accountId) => {
    if (!accountId) return;
    try {
      const response = await fetch(`${API_URL}/api/snapchat/conversion-assets/${accountId}`);
      if (!response.ok) throw new Error(await readError(response));
      setConversionAssets(await response.json());
    } catch (error) {
      console.error('Snapchat conversion assets unavailable:', error.message);
      setConversionAssets({ pixels: [], lead_forms: [], mobile_apps: [] });
    }
  }, []);

  const fetchTargetingOptions = useCallback(async (countryCode = 'ng') => {
    setIsLoadingTargeting(true);
    try {
      const response = await fetch(`${API_URL}/api/snapchat/targeting/options?country_code=${countryCode}`);
      if (!response.ok) throw new Error(await readError(response));
      setTargetingOptions(await response.json());
    } catch (error) {
      toast.error('Could not load Snapchat targeting options', { description: error.message });
    } finally {
      setIsLoadingTargeting(false);
    }
  }, []);

  const fetchSegments = useCallback(async (accountId) => {
    if (!accountId) return;
    try {
      const response = await fetch(`${API_URL}/api/snapchat/segments/${accountId}`);
      if (!response.ok) throw new Error(await readError(response));
      setSegments((await response.json()).segments || []);
    } catch (error) {
      console.error('Snapchat segments unavailable:', error.message);
      setSegments([]);
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await fetch(`${API_URL}/api/snapchat/accounts`);
      if (!response.ok) throw new Error(await readError(response));
      const data = await response.json();
      setAccounts(data.accounts || []);
      setOrganizations(data.organizations || []);
      setSelectedAccount((prev) => {
        if (prev && data.accounts?.some((a) => a.id === prev.id)) return prev;
        return data.accounts?.find((a) => a.status === 'ACTIVE') || data.accounts?.[0] || null;
      });
    } catch (error) {
      console.error('Failed to fetch Snapchat accounts:', error);
      toast.error('Could not load Snapchat ad accounts', { description: error.message });
    } finally {
      setIsLoadingAccounts(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/snapchat/connection-status`);
      const data = await response.json();
      setConnection(data);
      if (data.connected) {
        setSelectedProfileIdState(data.default_profile_id || '');
        if (data.organization_id) fetchPublicProfiles(data.organization_id);
        setPlatformSettings?.((prev) => ({
          ...prev,
          snapchat: { ...prev.snapchat, enabled: true, connected: true, accountName: data.display_name || data.email || 'Connected' },
        }));
        await fetchAccounts();
      } else {
        setPlatformSettings?.((prev) => ({ ...prev, snapchat: { ...prev.snapchat, connected: false, accountName: '' } }));
        setAccounts([]);
        setSelectedAccount(null);
      }
    } catch (error) {
      console.error('Failed to fetch Snapchat status:', error);
    }
  }, [fetchAccounts, fetchPublicProfiles, setPlatformSettings]);

  const connect = async ({ withProfileScope = false } = {}) => {
    try {
      toast.info('Connecting to Snapchat...', { description: 'Opening Snapchat login...' });
      const response = await fetch(`${API_URL}/api/snapchat/auth${withProfileScope ? '?include_profile_scope=true' : ''}`);
      if (!response.ok) throw new Error(await readError(response));
      const data = await response.json();
      const width = 600, height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(data.authorization_url, 'Snapchat Login', `width=${width},height=${height},left=${left},top=${top}`);
    } catch (error) {
      toast.error('Snapchat Connection Failed', { description: error.message });
    }
  };

  const disconnect = async () => {
    try {
      await fetch(`${API_URL}/api/snapchat/disconnect`, { method: 'POST' });
      setConnection({ connected: false });
      setAccounts([]);
      setSelectedAccount(null);
      setCampaigns([]);
      setPlatformSettings?.((prev) => ({ ...prev, snapchat: { ...prev.snapchat, connected: false, accountName: '' } }));
      toast.success('Snapchat disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Snapchat');
    }
  };

  const fetchCampaigns = useCallback(async (accountId = selectedAccount?.id) => {
    if (!accountId) return;
    setIsLoadingCampaigns(true);
    try {
      const response = await fetch(`${API_URL}/api/snapchat/campaigns/${accountId}`);
      if (!response.ok) throw new Error(await readError(response));
      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      toast.error('Could not load Snapchat campaigns', { description: error.message });
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [selectedAccount]);

  const setEntityStatus = async (entity, id, status) => {
    const response = await fetch(`${API_URL}/api/snapchat/${entity}/${id}/status?status=${status}`, { method: 'PATCH' });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const activateCampaign = async (campaignId) => {
    const response = await fetch(`${API_URL}/api/snapchat/activate-campaign/${campaignId}`, { method: 'POST' });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const uploadMedia = async (file, accountId, kind = 'top_snap') => {
    if (file.size > SNAPCHAT_LIMITS.maxUploadMB * 1024 * 1024) {
      throw new Error(`Snapchat media must be ${SNAPCHAT_LIMITS.maxUploadMB}MB or smaller`);
    }
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${API_URL}/api/snapchat/media/${accountId}?kind=${kind}`, { method: 'POST', body });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const submitCampaign = async (formData, onProgress) => {
    const accountId = selectedAccount?.id;
    if (!accountId) throw new Error('Select a Snapchat ad account first');
    onProgress?.('Uploading media to Snapchat...');
    const media = await uploadMedia(formData.videoFile || formData.imageFile, accountId);
    onProgress?.('Creating creative & campaign...');
    const goals = SNAPCHAT_OPTIMIZATION_GOALS[formData.objective] || [];
    const d = { ...defaultSnapDelivery(), ...(formData.snapDelivery || {}) };
    const c = { ...defaultSnapConversion(), ...(formData.snapConversion || {}) };
    const payload = {
      ...buildSnapTargetingPayload(formData),
      ad_account_id: accountId,
      campaign_name: formData.campaignName,
      objective: formData.objective,
      optimization_goal: goals.some((g) => g.value === formData.snapOptimizationGoal) ? formData.snapOptimizationGoal : goals[0]?.value,
      ad_squad_name: formData.adSetName || undefined,
      budget_type: formData.budgetType || 'daily',
      budget_amount: parseFloat(formData.budgetAmount),
      start_time: toStartIso(formData.startDate),
      end_time: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      placement: d.placement,
      snapchat_positions: d.placement === 'CUSTOM' ? d.positions : [],
      pacing_type: d.pacing,
      bid_strategy: d.bidStrategy,
      bid_amount: d.bidStrategy === 'AUTO_BID' ? null : parseFloat(d.bidAmount),
      frequency_cap: d.frequencyCapEnabled ? { count: parseInt(d.frequencyCapCount) || 1, interval_days: parseInt(d.frequencyCapDays) || 1 } : null,
      brand_safety: d.brandSafety,
      brand_name: formData.snapBrandName,
      headline: formData.headline,
      call_to_action: formData.snapCta || 'MORE',
      destination_url: formData.destinationUrl || null,
      media_id: media.media_id,
      pixel_id: c.pixelId || null,
      lead_form_id: c.leadFormId || null,
      snap_app_id: c.snapAppId || null,
      app_name: c.appName || null,
      ios_app_id: c.iosAppId || null,
      android_app_url: c.androidAppUrl || null,
      icon_media_id: c.iconMediaId || null,
      profile_id: selectedProfileId || null,
    };
    const response = await fetch(`${API_URL}/api/snapchat/submit-ad`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SNAPCHAT_AUTH_SUCCESS') {
        toast.success('Snapchat Connected!', { description: event.data.display_name ? `Connected as ${event.data.display_name}` : 'Your Snapchat account is now connected.' });
        fetchStatus();
      } else if (event.data?.type === 'SNAPCHAT_AUTH_ERROR') {
        toast.error('Snapchat Connection Failed', { description: event.data.error || 'Please try again.' });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchStatus]);

  return {
    connection, accounts, organizations, selectedAccount, setSelectedAccount, campaigns,
    isLoadingCampaigns, isLoadingAccounts, connect, disconnect, fetchAccounts, fetchCampaigns,
    setEntityStatus, activateCampaign, submitCampaign,
    targetingOptions, segments, isLoadingTargeting, fetchTargetingOptions, fetchSegments,
    audienceEstimate, estimateAudience, conversionAssets, fetchConversionAssets, uploadMedia,
    publicProfiles, profileScopeNeeded, selectedProfileId, saveDefaultProfile, fetchPublicProfiles,
  };
};
